import { zodResolver } from '@hookform/resolvers/zod';
import { CloudUpload, MailIcon, UserIcon } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useUser } from '../../hooks/use-user';
import useApiRequest from '../../lib/hooks/useRequest';
import { UserProfileResponseDto, UserType } from '../../lib/interfaces/user';
import NotificationsIcon from '../../ui/components/icons/notifications-icon';
import { Button } from '../../ui/components/ui/button';
import DataFetchParent from '../../ui/components/ui/data-fetch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../ui/components/ui/form';
import { Input } from '../../ui/components/ui/input';
import { Switch } from '../../ui/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/components/ui/tabs';

const Settings = () => {
  const { apiRequest } = useApiRequest();
  const [userInfos, setUserInfos] = useState<UserProfileResponseDto>();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);

  const getUserSettings = useCallback(async () => {
    setLoading(true);
    const response = await apiRequest<UserProfileResponseDto>('user-profile', 'GET');
    if (response) {
      setUserInfos(response.data);
    }
    setLoading(false);
  }, [apiRequest]);

  useEffect(() => {
    getUserSettings();
  }, [getUserSettings, reload]);

  return (
    <div className="flex flex-col gap-9 h-full">
      <div>
        <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">Einstellungen</h1>
        <p className="text-[16px] text-[#888]">Verwalte Dein Konto</p>
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList className="gap-4">
          <TabsTrigger value="account">
            <UserIcon />
            Meine Details
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <NotificationsIcon />
            Benachrichtigungen
          </TabsTrigger>
          {/* <TabsTrigger value="invoice">
            <PackagePlus />
            Abrechnung
          </TabsTrigger> */}
        </TabsList>
        <DataFetchParent
          data={userInfos}
          isLoading={loading}
          renderElement={(data: UserProfileResponseDto) => (
            <>
              <TabsContent value="account">
                <DetailsController userInfo={data} setReload={setReload} />
              </TabsContent>
              <TabsContent value="notifications">
                <NotifactionsController userInfo={data} setReload={setReload} />
              </TabsContent>
              {/* <TabsContent value="invoice">
                <div className="flex flex-col py-6 px-12 bg-white rounded-xl gap-3">
                  <p className="text-2xl text-foreground font-bold">Abrechnung</p>

                  <div className="flex gap-2 flex-col items-start">
                    <p className="font-bold leading-9">Zahlungsmethode</p>

                    <p className="text-sm leading-7 text-[#888888]">Ändere Deine Zahlungsmethode und Rechnungsdaten</p>

                    <div className="flex rounded-xl border border-gray-200 p-4 gap-4">
                      <div>
                        <VisaIcon />
                      </div>
                      <div className="text-xs">
                        Visa ending in 1234
                        <br />
                        Expiry 06/2024
                        <br />
                        <span className="flex items-center gap-1 mt-1">
                          <MailIcon size="15" />
                          billing@untitledui.com
                        </span>
                      </div>
                      <div>
                        <Button variant="outline" className="text-sm font-semibold">
                          bearbeiten
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col py-6 px-12 bg-white rounded-xl gap-5 mt-[34px]">
                  <div className="flex justify-between items-center">
                    <p className="text-2xl text-foreground font-bold">Rechnungen</p>
                    <Button
                      variant="outline"
                      className="flex items-center gap-4 text-[#888] border-2 border-[#D0D5DD] rounded-xl text-[17px]"
                    >
                      <DownloadIcon />
                      <span>Alle downloaden</span>
                    </Button>
                  </div>

                  <div className="flex gap-10 w-full flex-col">
                    <DataTable data={invoices} />
                  </div>
                </div>
              </TabsContent> */}
            </>
          )}
        />
      </Tabs>
    </div>
  );
};

const DetailsController = ({ userInfo, setReload }: { userInfo: UserProfileResponseDto; setReload: Dispatch<SetStateAction<boolean>> }) => {
  const { apiRequest, apiFileRequest } = useApiRequest();
  const { user } = useUser();
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [changedMail, setChangedMail] = useState(false);
  const [changedPseudoName, setChangedPseudoName] = useState(false);
  const [showEmailConfirmDialog, setShowEmailConfirmDialog] = useState(false);
  const [pendingEmailData, setPendingEmailData] = useState<z.infer<typeof mailForm> | null>(null);

  const mailForm = z.object({
    mail: z.string().email('Ungültige E-Mail-Adresse')
  });

  const pseudoNameForm = z.object({
    pseudoName: z.string()
  });

  const form = useForm<z.infer<typeof mailForm>>({
    resolver: zodResolver(mailForm),
    defaultValues: {
      mail: userInfo.mail
    }
  });

  const pseudoNameFormInstance = useForm<z.infer<typeof pseudoNameForm>>({
    resolver: zodResolver(pseudoNameForm),
    defaultValues: {
      pseudoName: userInfo.pseudoName || ''
    }
  });

  const resetPassword = async () => {
    await apiRequest('user-profile/reset-password', 'POST', {
      toast: {
        toastText: 'Email zum Passwort zurücksetzen wurde gesendet'
      }
    });
  };

  const uploadFiles = useCallback(async () => {
    const res = await apiFileRequest<string>(`user-profile/image`, 'POST', file);
    setReload((prev) => !prev);
    if (res.error) {
      // alert("Fehler beim Upload");
      console.log(res.error);
    } else {
      window.location.reload();
    }
  }, [apiFileRequest, file, setReload]);

  const handleUpload = (file: File) => {
    setFile(file);
  };

  const handleUploadClick = () => {
    const uploadBtn = document.querySelector(`#upload-chat`) as HTMLButtonElement | null;
    if (uploadBtn) {
      uploadBtn.click();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handleFileDrop(event.dataTransfer.files);
    }
  };

  const handleFileDrop = (files: FileList | null) => {
    if (files?.length === 0 || !files) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const filteredFiles = Array.from(files).filter((file) => allowedTypes.includes(file.type));
    handleUpload(filteredFiles[0]);
  };

  const deleteProfilePicture = async () => {
    await apiRequest('user-profile/image', 'DELETE', {
      toast: {
        toastText: 'Profilbild wurde gelöscht'
      }
    });
    setReload((prev) => !prev);
    window.location.reload();
  };

  const onSubmit = async (data: z.infer<typeof mailForm>) => {
    setPendingEmailData(data);
    setShowEmailConfirmDialog(true);
  };

  const confirmEmailChange = async () => {
    if (!pendingEmailData) return;

    const response = await apiRequest('user-profile/email', 'PATCH', {
      body: {
        mail: pendingEmailData.mail
      },
      toast: {
        toastText: 'E-Mail wurde aktualisiert'
      }
    });
    if (response.error) {
      console.log(response.error);
      setShowEmailConfirmDialog(false);
      setPendingEmailData(null);
    } else {
      setReload((prev) => !prev);
      setShowEmailConfirmDialog(false);
      setPendingEmailData(null);
      window.location.reload();
    }
  };

  const onPseudoNameSubmit = async (data: z.infer<typeof pseudoNameForm>) => {
    await apiRequest('user-profile/pseudo-name', 'PATCH', {
      body: {
        pseudoName: data.pseudoName
      },
      toast: {
        toastText: 'Pseudonym wurde aktualisiert'
      }
    });
    setReload((prev) => !prev);
    window.location.reload();
  };

  useEffect(() => {
    if (file) {
      uploadFiles();
    }
  }, [file, uploadFiles]);

  return (
    <div className="flex flex-col py-6 px-12 bg-white rounded-xl gap-5">
      <p className="text-2xl text-foreground font-bold">Profil</p>

      <div className="flex gap-10 w-full flex-col md:flex-row">
        <div className="flex w-full md:w-1/2 flex-col gap-2">
          <p className="font-bold leading-9">Name des Benutzers:*</p>
          <div className="flex gap-3 w-full">
            <div className="rounded-xl px-3 py-2 border border-[#D0D5DD] text-sm w-1/2">
              <input placeholder="Vorname" className="border-none outline-none w-full bg-white" value={userInfo.firstName} disabled />
            </div>
            <div className="rounded-xl px-3 py-2 border border-[#D0D5DD] text-sm w-1/2">
              <input placeholder="Nachname" className="border-none outline-none w-full bg-white" value={userInfo.lastName} disabled />
            </div>
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
              <p className="font-bold leading-9 mt-3">E-Mail:*</p>
              <div className="flex gap-3">
                <FormField
                  control={form.control}
                  name="mail"
                  render={({ field }) => (
                    <FormItem className="flex flex-col bg-white rounded-lg gap-[14px] grow">
                      <FormControl>
                        <div className="rounded-xl px-3 py-2 border border-[#D0D5DD] text-sm">
                          <input
                            placeholder="E-Mail"
                            className="border-none outline-none w-full bg-white"
                            {...field}
                            onChange={(e) => {
                              setChangedMail(e.target.value !== userInfo.mail);
                              field.onChange(e);
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-fit" disabled={!changedMail}>
                  Ändern
                </Button>
              </div>
            </form>
          </Form>
          {user?.userType === UserType.Internal && (
            <Form {...pseudoNameFormInstance}>
              <form onSubmit={pseudoNameFormInstance.handleSubmit(onPseudoNameSubmit)} className="flex flex-col gap-5 h-full">
                <p className="font-bold leading-9 mt-3">Pseudonym:</p>
                <div className="flex gap-3">
                  <FormField
                    control={pseudoNameFormInstance.control}
                    name="pseudoName"
                    render={({ field }) => (
                      <FormItem className="flex flex-col bg-white rounded-lg gap-[14px] grow">
                        <FormControl>
                          <div className="rounded-xl px-3 py-2 border border-[#D0D5DD] text-sm">
                            <input
                              placeholder="Pseudonym"
                              className="border-none outline-none w-full bg-white"
                              {...field}
                              onChange={(e) => {
                                setChangedPseudoName(e.target.value !== (userInfo.pseudoName || ''));
                                field.onChange(e);
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-fit" disabled={!changedPseudoName}>
                    Ändern
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </div>
        <div className="flex w-full md:w-1/2 flex-col gap-2">
          {userInfo.profilePictureUrl ? (
            <>
              <img src={userInfo.profilePictureUrl} alt="profile" className="rounded-xl w-32 h-32" />
              <Button variant="outline" onClick={deleteProfilePicture}>
                Profilbild löschen
              </Button>
            </>
          ) : (
            <>
              <p className="font-bold leading-9">Profilbild hochladen:</p>
              <div
                className={`flex flex-col rounded-xl px-6 py-4 border border-[#D0D5DD] text-sm justify-center items-center ${
                  isDragging ? 'border-[#C0DE60] border-dotted' : ''
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadClick}>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUploadClick();
                  }}>
                  <CloudUpload />
                </Button>
                <p className="text-sm mt-3">
                  <span className="text-[#C0DE60] font-medium">Zum Upload klicken</span> oder hier reinziehen
                </p>
                <p className="text-xs">PDF, SVG, PNG, JPG or GIF (max. 5 MB)</p>
                <Input
                  type="file"
                  className="hidden"
                  accept="image/png, image/jpeg"
                  id="upload-chat"
                  onChange={(e) => {
                    e.preventDefault();
                    if (e.target?.files && e.target?.files.length > 0) {
                      handleUpload(e.target.files[0]);
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="mt-5">
        <Button className="flex items-center gap-4" onClick={resetPassword}>
          <MailIcon />
          Passwort zurücksetzen
        </Button>
      </div>
      <Dialog open={showEmailConfirmDialog} onOpenChange={setShowEmailConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>E-Mail-Adresse ändern</DialogTitle>
            <DialogDescription>Bist du sicher, dass du deine E-Mail-Adresse ändern möchtest?</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">
              Neue E-Mail-Adresse: <strong>{pendingEmailData?.mail}</strong>
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-yellow-800">
                <strong>Wichtig:</strong> Nach der Änderung wirst Du automatisch ausgeloggt und musst Deine neue E-Mail-Adresse erneut verifizieren.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEmailConfirmDialog(false);
                setPendingEmailData(null);
              }}>
              Abbrechen
            </Button>
            <Button onClick={confirmEmailChange}>Bestätigen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NotifactionsController = ({ userInfo, setReload }: { userInfo: UserProfileResponseDto; setReload: Dispatch<SetStateAction<boolean>> }) => {
  const { apiRequest } = useApiRequest();
  const { user } = useUser();
  const [appNotificationChatEnabled, setAppNotificationChatEnabled] = useState(userInfo.appNotificationChatEnabled);
  const [appNotificationStatusEnabled, setAppNotificationStatusEnabled] = useState(userInfo.appNotificationStatusEnabled);
  const [mailNotificationChatEnabled, setMailNotificationChatEnabled] = useState(userInfo.mailNotificationChatEnabled);
  const [mailNotificationStatusEnabled, setMailNotificationStatusEnabled] = useState(userInfo.mailNotificationStatusEnabled);
  const [appNotificationNewJobEnabled, setAppNotificationNewJobEnabled] = useState(userInfo.appNotificationNewJobEnabled);
  const [appNotificationRatingEnabled, setAppNotificationRatingEnabled] = useState(userInfo.appNotificationRatingEnabled);
  const [mailNotificationNewJobEnabled, setMailNotificationNewJobEnabled] = useState(userInfo.mailNotificationNewJobEnabled);
  //const [appNotificationFinalUrlEnabled, setAppNotificationFinalUrlEnabled] = useState(userInfo.appNotificationFinalUrlEnabled);
  //const [mailNotificationFinalUrlEnabled, setMailNotificationFinalUrlEnabled] = useState(userInfo.mailNotificationFinalUrlEnabled);

  const [mailNotificationRatingEnabled, setMailNotificationRatingEnabled] = useState(userInfo.mailNotificationRatingEnabled);

  const updateUser = useCallback(async () => {
    await apiRequest('user-profile', 'PATCH', {
      body: {
        appNotificationChatEnabled,
        appNotificationStatusEnabled,
        appNotificationNewJobEnabled,
        //appNotificationFinalUrlEnabled,
        appNotificationRatingEnabled,
        mailNotificationChatEnabled,
        mailNotificationStatusEnabled,
        mailNotificationNewJobEnabled,
        //mailNotificationFinalUrlEnabled,
        mailNotificationRatingEnabled
      },
      toast: {
        toastText: 'Benachrichtigungseinstellungen wurden aktualisiert'
      }
    });
    setReload((prev) => !prev);
  }, [
    apiRequest,
    appNotificationChatEnabled,
    appNotificationStatusEnabled,
    appNotificationNewJobEnabled,
    //appNotificationFinalUrlEnabled,
    appNotificationRatingEnabled,
    mailNotificationChatEnabled,
    mailNotificationStatusEnabled,
    mailNotificationNewJobEnabled,
    //mailNotificationFinalUrlEnabled,
    mailNotificationRatingEnabled,
    setReload
  ]);

  useEffect(() => {
    if (
      userInfo.appNotificationChatEnabled !== appNotificationChatEnabled ||
      userInfo.appNotificationStatusEnabled !== appNotificationStatusEnabled ||
      userInfo.mailNotificationChatEnabled !== mailNotificationChatEnabled ||
      userInfo.mailNotificationStatusEnabled !== mailNotificationStatusEnabled ||
      userInfo.appNotificationNewJobEnabled !== appNotificationNewJobEnabled ||
      //userInfo.appNotificationFinalUrlEnabled !== appNotificationFinalUrlEnabled ||
      userInfo.mailNotificationNewJobEnabled !== mailNotificationNewJobEnabled ||
      //userInfo.mailNotificationFinalUrlEnabled !== mailNotificationFinalUrlEnabled ||
      userInfo.mailNotificationRatingEnabled !== mailNotificationRatingEnabled ||
      userInfo.appNotificationRatingEnabled !== appNotificationRatingEnabled
    ) {
      updateUser();
    }
  }, [
    appNotificationChatEnabled,
    appNotificationStatusEnabled,
    mailNotificationChatEnabled,
    mailNotificationStatusEnabled,
    appNotificationNewJobEnabled,
    //appNotificationFinalUrlEnabled,
    mailNotificationNewJobEnabled,
    updateUser,
    userInfo.appNotificationChatEnabled,
    userInfo.appNotificationStatusEnabled,
    userInfo.mailNotificationChatEnabled,
    userInfo.mailNotificationStatusEnabled,
    userInfo.appNotificationNewJobEnabled,
    userInfo.appNotificationFinalUrlEnabled,
    userInfo.mailNotificationNewJobEnabled,
    userInfo.mailNotificationFinalUrlEnabled,
    //mailNotificationFinalUrlEnabled,
    userInfo.mailNotificationRatingEnabled,
    mailNotificationRatingEnabled,
    userInfo.appNotificationRatingEnabled,
    appNotificationRatingEnabled
  ]);

  return (
    <div className="flex flex-col py-6 px-12 bg-white rounded-xl gap-5">
      <p className="text-2xl text-foreground font-bold">Benachrichtigungen</p>
      <div className="flex gap-10 w-full flex-col">
        <div className="flex flex-col gap-2">
          <p className="font-bold leading-9">Auftragsstatus</p>
          <div className="flex gap-3 w-full justify-between">
            <p className="text-sm leading-7 text-[#888888]">
              Erhalte Benachrichtigungen über Statusänderungen Deiner Aufträge, einschließlich Annahme, Bearbeitung und Fertigstellung. So bleibst Du
              immer über den aktuellen Stand informiert.
            </p>
            <div className="flex flex-col gap-2 min-w-32">
              <div className="flex justify-between items-center">
                <span className="text-[#888] font-bold">App</span>
                <Switch checked={appNotificationStatusEnabled} onCheckedChange={(e) => setAppNotificationStatusEnabled(e)} />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#888] font-bold">E-Mail</span>
                <Switch checked={mailNotificationStatusEnabled} onCheckedChange={(e) => setMailNotificationStatusEnabled(e)} />
              </div>
            </div>
          </div>
        </div>
        <div className="flex h-[2px] bg-[#E7E7E7]"></div>
        <div className="flex flex-col gap-2">
          <p className="font-bold leading-9">Neue Nachricht</p>
          <div className="flex gap-3 w-full justify-between">
            <p className="text-sm leading-7 text-[#888888]">
              Werde benachrichtigt, wenn neue Nachrichten von Kunden oder Teammitgliedern eingehen, um zeitnah auf wichtige Anfragen und Updates
              reagieren zu können.
            </p>

            <div className="flex flex-col gap-2 min-w-32">
              <div className="flex justify-between items-center">
                <span className="text-[#888] font-bold">App</span>
                <Switch checked={appNotificationChatEnabled} onCheckedChange={(e) => setAppNotificationChatEnabled(e)} />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#888] font-bold">E-Mail</span>
                <Switch checked={mailNotificationChatEnabled} onCheckedChange={(e) => setMailNotificationChatEnabled(e)} />
              </div>
            </div>
          </div>
        </div>
        <div className="flex h-[2px] bg-[#E7E7E7]"></div>
        <div className="flex flex-col gap-2">
          <p className="font-bold leading-9">Neuer Job</p>
          <div className="flex gap-3 w-full justify-between">
            <p className="text-sm leading-7 text-[#888888]">Benachrichtige mich über neue Jobs, sobald sie erstellt werden.</p>
            <div className="flex flex-col gap-2 min-w-32">
              <div className="flex justify-between items-center">
                <span className="text-[#888] font-bold">App</span>
                <Switch checked={appNotificationNewJobEnabled} onCheckedChange={(e) => setAppNotificationNewJobEnabled(e)} />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#888] font-bold">E-Mail</span>
                <Switch checked={mailNotificationNewJobEnabled} onCheckedChange={(e) => setMailNotificationNewJobEnabled(e)} />
              </div>
            </div>
          </div>
        </div>
        {user?.userType === UserType.Customer && <></>}
        {user?.userType === UserType.Internal && (
          <>
            <div className="flex h-[2px] bg-[#E7E7E7]"></div>
            <div className="flex flex-col gap-2">
              <p className="font-bold leading-9">Rating</p>
              <div className="flex gap-3 w-full justify-between">
                <p className="text-sm leading-7 text-[#888888]">
                  Erhalte Benachrichtigungen, wenn ein Kunde Deinen Auftrag abschließt und Du einen Job-Bewertung erhalten hast.
                </p>
                <div className="flex flex-col gap-2 min-w-32">
                  <div className="flex justify-between items-center">
                    <span className="text-[#888] font-bold">App</span>
                    <Switch checked={appNotificationRatingEnabled} onCheckedChange={(e) => setAppNotificationRatingEnabled(e)} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#888] font-bold">E-Mail</span>
                    <Switch checked={mailNotificationRatingEnabled} onCheckedChange={(e) => setMailNotificationRatingEnabled(e)} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Settings;
